/**
 * OccasionGuideService
 *
 * Demande à l'IA UNE FOIS PAR SEMAINE ce qu'est "Casual/Bureau/Chic/Soirée/Sport"
 * en termes de vêtements / matières / fits / formality.
 *
 * Ce guide est ensuite utilisé par le classifier LooksScreen pour scorer les tenues
 * en matchant les ai_metadata des articles contre les critères de chaque occasion.
 *
 * Cache : 7 jours en AsyncStorage.
 * Fallback : guide statique si appel échoue.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';

const API = AWS_CONFIG.apiUrl;

const CACHE_KEY = 'outfit_occasion_guide_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

// ═══ Guide statique par défaut (utilisé si API échoue) ═══
// Pensé en 2026 pour la mode contemporaine FR/EU
const STATIC_GUIDE = {
  generatedAt: 0,
  occasions: {
    Casual: {
      description: 'Tenues décontractées pour le quotidien, week-end, balades',
      keywords: ['casual', 'décontracté', 'everyday', 'daily', 'streetwear', 'urban', 'relax'],
      formalityRange: [0, 4],
      typicalPieces: ['t-shirt', 'jean', 'hoodie', 'sneakers', 'sweat', 'polo', 'bermuda', 'basket', 'converse'],
      materials: ['coton', 'denim', 'jersey'],
      fits: ['relaxed', 'oversize', 'regular'],
      colors: ['toutes'],
    },
    Bureau: {
      description: 'Tenues de travail professionnel, réunions, rendez-vous clients',
      keywords: ['business', 'professionnel', 'office', 'formal work', 'smart casual', 'workwear'],
      formalityRange: [5, 8],
      typicalPieces: ['blazer', 'chemise', 'chemisier', 'tailleur', 'costume', 'pantalon tailleur', 'trench', 'mocassin', 'derby', 'oxford', 'ballerine', 'robe chemise'],
      materials: ['laine', 'coton', 'lin', 'crêpe'],
      fits: ['slim', 'fitted', 'regular'],
      colors: ['noir', 'gris', 'beige', 'navy', 'blanc'],
    },
    Chic: {
      description: 'Tenues élégantes pour dîner, événement culturel, sortie habillée',
      keywords: ['chic', 'élégant', 'elegant', 'sophistiqué', 'classy', 'raffiné', 'minimaliste'],
      formalityRange: [6, 8],
      typicalPieces: ['robe', 'escarpin', 'ballerine chic', 'jupe midi', 'blouse', 'pantalon fluide', 'richelieu'],
      materials: ['soie', 'satin', 'cuir', 'laine fine', 'crêpe'],
      fits: ['fitted', 'slim', 'oversize structuré'],
      colors: ['noir', 'beige', 'crème', 'bordeaux', 'navy', 'gris'],
    },
    Soirée: {
      description: 'Tenues pour cocktail, gala, soirée, événement festif',
      keywords: ['soirée', 'cocktail', 'gala', 'evening', 'party', 'habillé', 'festive', 'glamour'],
      formalityRange: [8, 10],
      typicalPieces: ['robe de soirée', 'robe cocktail', 'smoking', 'tuxedo', 'bustier', 'escarpin', 'stiletto', 'sequin', 'paillette'],
      materials: ['satin', 'soie', 'velours', 'dentelle', 'taffetas', 'organza', 'sequin'],
      fits: ['fitted', 'slim'],
      colors: ['noir', 'rouge', 'bordeaux', 'doré', 'argent', 'marine'],
    },
    Sport: {
      description: 'Tenues athlétiques, entraînement, running, gym',
      keywords: ['sport', 'athletic', 'gym', 'fitness', 'running', 'athleisure', 'workout', 'yoga'],
      formalityRange: [0, 3],
      typicalPieces: ['legging', 'jogging', 'brassière sport', 'short sport', 'training shoe', 'running shoe', 'survêtement', 'maillot sport', 'tracksuit'],
      materials: ['polyester', 'lycra', 'technique', 'dri-fit', 'climacool', 'mesh'],
      fits: ['fitted', 'slim', 'compression'],
      colors: ['toutes', 'noir', 'neon'],
    },
  },
};

/**
 * Récupère le guide depuis le cache ou l'API.
 * Retourne toujours un guide valide (fallback static).
 */
export async function getOccasionGuide() {
  // Essai cache local
  try {
    const raw = await AsyncStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && cached.generatedAt && Date.now() - cached.generatedAt < CACHE_TTL_MS) {
        console.log(`[OccasionGuide] 💾 cache hit (age=${Math.round((Date.now() - cached.generatedAt) / 3600000)}h)`);
        return cached;
      }
    }
  } catch (e) {
    console.warn('[OccasionGuide] cache read error:', e.message);
  }

  // Appel API via trending-products endpoint (action: occasion-guide)
  try {
    console.log('[OccasionGuide] 🚀 fetching fresh guide from IA...');
    const res = await fetch(`${API}/ai/trending-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'occasion-guide', locale: 'fr' }),
    });
    if (res.ok) {
      const json = await res.json();
      if (json && json.occasions && typeof json.occasions === 'object') {
        const guide = { generatedAt: Date.now(), occasions: json.occasions };
        try {
          await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(guide));
        } catch {}
        console.log(`[OccasionGuide] ✨ fresh guide saved (${Object.keys(guide.occasions).length} occasions)`);
        return guide;
      }
    }
    console.warn(`[OccasionGuide] API returned ${res.status}, using static fallback`);
  } catch (e) {
    console.warn('[OccasionGuide] API error, using static fallback:', e.message);
  }

  // Fallback statique
  return STATIC_GUIDE;
}

/**
 * Calcule un score de match entre un item et chaque occasion,
 * en utilisant le guide IA + ai_metadata de l'item.
 *
 * Retourne { Casual: 0-10, Bureau: 0-10, Chic: 0-10, Soirée: 0-10, Sport: 0-10 }
 */
export function scoreItemAgainstGuide(item, guide) {
  const scores = { Casual: 0, Bureau: 0, Chic: 0, Soirée: 0, Sport: 0 };
  if (!guide || !guide.occasions) return scores;

  const meta = item.ai_metadata || {};
  const name = (item.name || '').toLowerCase();
  const brand = (item.brand || '').toLowerCase();
  const fullText = `${name} ${brand}`;
  const itemTags = Array.isArray(meta.styleTags) ? meta.styleTags.map(t => (t || '').toLowerCase()) : [];
  const itemMaterial = (meta.material || '').toLowerCase();
  const itemFit = (meta.fit || '').toLowerCase();
  const itemFormality = typeof meta.formality === 'number' ? meta.formality : null;

  for (const [occasion, rules] of Object.entries(guide.occasions)) {
    let score = 0;

    // Match keywords dans styleTags
    const kwMatches = (rules.keywords || []).filter(kw =>
      itemTags.some(tag => tag.includes(kw.toLowerCase()))
    ).length;
    score += kwMatches * 3;

    // Match typical pieces dans nom
    const pieceMatches = (rules.typicalPieces || []).filter(piece =>
      fullText.includes(piece.toLowerCase())
    ).length;
    score += pieceMatches * 4;

    // Match materials
    const matMatches = (rules.materials || []).filter(mat =>
      itemMaterial.includes(mat.toLowerCase()) || fullText.includes(mat.toLowerCase())
    ).length;
    score += matMatches * 3;

    // Match fits
    const fitMatches = (rules.fits || []).filter(fit => itemFit.includes(fit.toLowerCase())).length;
    score += fitMatches * 2;

    // Formality dans range
    if (itemFormality !== null && Array.isArray(rules.formalityRange)) {
      const [min, max] = rules.formalityRange;
      if (itemFormality >= min && itemFormality <= max) score += 3;
      else if (itemFormality >= min - 1 && itemFormality <= max + 1) score += 1;
    }

    scores[occasion] = score;
  }

  return scores;
}

/**
 * Force le rafraîchissement du guide (appel IA)
 * Utile pour test manuel ou bouton admin
 */
export async function forceRefreshGuide() {
  try {
    await AsyncStorage.removeItem(CACHE_KEY);
  } catch {}
  return getOccasionGuide();
}
